import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { fairLendingApi } from '../../lib/api';
import { useTeamId } from '../../hooks/useTeamId';
import RiskBadge from './RiskBadge';
import toast from 'react-hot-toast';

interface Finding {
  id: string;
  analysisType: string;
  demographicGroup: string | null;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  metric: string;
  metricValue: number | null;
  threshold: number | null;
  pValue: number | null;
  sampleSize: number | null;
  title: string;
  description: string;
  regulatoryRef: string | null;
  status: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
}

const STATUS_OPTIONS = [
  { value: 'OPEN', label: 'Open' },
  { value: 'ACKNOWLEDGED', label: 'Acknowledged' },
  { value: 'MITIGATED', label: 'Mitigated' },
  { value: 'FALSE_POSITIVE', label: 'False Positive' },
] as const;

export default function FindingCard({ finding, analysisId }: { finding: Finding; analysisId: string }) {
  const queryClient = useQueryClient();
  const { teamId } = useTeamId();
  const [status, setStatus] = useState(finding.status);
  const [note, setNote] = useState(finding.reviewNote || '');
  const [isEditing, setIsEditing] = useState(false);

  const reviewMutation = useMutation({
    mutationFn: () =>
      fairLendingApi.reviewFinding(teamId!, finding.id, {
        status: status as any,
        reviewNote: note,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fair-lending-analysis', analysisId] });
      toast.success('Finding reviewed');
      setIsEditing(false);
    },
    onError: () => toast.error('Failed to review finding'),
  });

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <RiskBadge level={finding.riskLevel} />
            <span className="text-xs text-gray-500 uppercase tracking-wide">
              {finding.analysisType.replace(/_/g, ' ')}
            </span>
          </div>
          <h4 className="text-sm font-semibold text-gray-900">{finding.title}</h4>
        </div>
        <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 font-medium">
          {finding.status.replace(/_/g, ' ')}
        </span>
      </div>

      <p className="text-sm text-gray-700 mb-3">{finding.description}</p>

      {/* Metrics row */}
      <div className="flex flex-wrap gap-4 text-xs text-gray-500 mb-3">
        {finding.metricValue !== null && (
          <span>Value: <strong className="text-gray-700">{finding.metricValue.toFixed(2)}</strong></span>
        )}
        {finding.threshold !== null && (
          <span>Threshold: <strong className="text-gray-700">{finding.threshold}</strong></span>
        )}
        {finding.pValue !== null && (
          <span>p-value: <strong className="text-gray-700">{finding.pValue.toFixed(4)}</strong></span>
        )}
        {finding.sampleSize !== null && (
          <span>n={finding.sampleSize}</span>
        )}
      </div>

      {finding.regulatoryRef && (
        <p className="text-xs text-gray-400 mb-3">{finding.regulatoryRef}</p>
      )}

      {/* Review controls */}
      {isEditing ? (
        <div className="border-t border-gray-100 pt-3 mt-3 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-ocean-500 focus:ring-ocean-500"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Review Note</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-ocean-500 focus:ring-ocean-500"
              placeholder="Add a review note..."
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => reviewMutation.mutate()}
              disabled={reviewMutation.isPending}
              className="rounded-md px-3 py-1.5 text-sm font-medium bg-ocean-600 text-white hover:bg-ocean-700 disabled:opacity-50"
            >
              {reviewMutation.isPending ? 'Saving...' : 'Save Review'}
            </button>
            <button
              onClick={() => setIsEditing(false)}
              className="rounded-md px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setIsEditing(true)}
          className="text-xs text-ocean-600 hover:text-ocean-700 font-medium mt-1"
        >
          Review Finding
        </button>
      )}
    </div>
  );
}
